import React, { useContext } from 'react'
import { Link } from 'react-router-dom'
import { Image } from '@deal/components'
import { getSellableUrl, formatSellablePricing } from '#src/services/sellable'
import { GuestSellable } from '#src/services/cart'
import { GuestSellableFragment } from '#src/generated/graphql'
import { AnalyticsContext } from '#src/containers/Analytics'
import { getSellableEcommerceProperties } from '#src/services/tracking'
import defaultProduct from '#src/assets/images/default-product.png'
import numeral from 'numeral'
import Remove from './remove.svg'
import Plus from './plus.svg'
import Minus from './minus.svg'
import styles from './styles.css'

interface GuestLineItemProps {
  sellable: GuestSellableFragment
  sellableInStorage?: GuestSellable
  onRemoveSellable: (sellableId: string) => void
  onUpdateQuantity: (sellableId: string, quantity: number) => void
}

const GuestLineItem: React.SFC<GuestLineItemProps> = ({
  sellable,
  sellableInStorage,
  onRemoveSellable,
  onUpdateQuantity
}) => {
  // If the sellable couldn't be found in storage, don't render it
  if (!sellableInStorage) {
    return null
  }

  const analytics = useContext(AnalyticsContext)
  const sellableImage = sellable.images[0].url || defaultProduct
  const formattedPrice = formatSellablePricing(sellable, { percentSavingsDisplayThreshold: 5 })

  return (
    <div className={styles.lineItem} data-testid="guest-cart-line-item">
      <div className={styles.imageContainer}>
        <Link to={getSellableUrl(sellable)} className={styles.link}>
          <Image
            src={sellableImage}
            className={styles.image}
            width={80}
            imgixParams={{ fit: 'clip' }}
          />
        </Link>
      </div>
      <div className={styles.details}>
        <Link
          to={getSellableUrl(sellable)}
          className={styles.title}
          data-testid="guest-cart-line-item-title"
        >
          {sellable.title}
        </Link>

        {sellable.variants && (
          <div className={styles.variants}>
            {sellable.variants.map((variant, idx) => (
              <span key={variant.displayValueHtml}>
                {variant.displayValueHtml}
                {idx === sellable.variants.length - 1 ? '' : ', '}
              </span>
            ))}
          </div>
        )}

        <div className={styles.price}>
          {formattedPrice && <span>{numeral(formattedPrice.price).format('$0,0.00')}</span>}
          {formattedPrice && formattedPrice.msrp && (
            <span className={styles.originalPrice}>
              {numeral(formattedPrice.msrp).format('$0,0.00')}
            </span>
          )}
        </div>

        <div className={styles.quantityContainer}>
          <div className={styles.incrementDecrement}>
            <button
              className={styles.quantityButton}
              disabled={sellableInStorage.quantity < 2}
              onClick={() => {
                onUpdateQuantity(sellable.id, sellableInStorage.quantity - 1)
              }}
            >
              <Minus />
            </button>
            <div className={styles.quantityNumber}>{sellableInStorage.quantity}</div>
            <button
              className={styles.quantityButton}
              onClick={() => {
                onUpdateQuantity(sellable.id, sellableInStorage.quantity + 1)
              }}
            >
              <Plus />
            </button>
          </div>
          <button
            className={styles.removeButton}
            onClick={() => {
              onRemoveSellable(sellable.id)

              analytics?.events.productRemoved(getSellableEcommerceProperties(sellable))
            }}
          >
            <Remove />
          </button>
        </div>
      </div>
    </div>
  )
}

export default GuestLineItem
